import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronRight, Clock, Loader2, MapPin, Plus, Search, Sparkles, X } from 'lucide-react';
import { api } from '../api';
import { useCapa } from '../ui/navegacionReact';
import Ayuda from '../ui/Ayuda';
import {
  ESPERA_BUSQUEDA_MS, RUTA, TIPOS_AL_VUELO, accionDeFila, migas, rutaLegible, textoSugerida,
  urlDelSelector,
} from './selectorUbicacion';

/* =====================================================================
   SELECTOR DE UBICACIÓN QUE ENTIENDE EL ÁRBOL · CATÁLOGO VIVO F3
   =====================================================================
   Sustituye al `<select>` plano de «código — nombre». Un campo que dice
   dónde («Habitación > Mesa de trabajo > Caja de resistencias») y, al
   tocarlo, una hoja a pantalla completa en el teléfono:

     · buscar por cualquier nivel («caja res»);
     · navegar con migas de pan y «Elegir aquí»;
     · recientes de la persona y la sugerida para la categoría, arriba;
     · «+ Nueva ubicación aquí» en el nivel donde se está parado, apagado
       con su motivo si no hay permiso, y «¿Es esta?» antes de duplicar.

   Lo que no se puede elegir (oficina, tránsito, bloqueada, llena) se VE
   apagado con su motivo: hay que poder atravesar «Habitación» para llegar
   a la caja, y un lugar que desaparece sin explicación no se encuentra.

   Uso:
     <SelectorUbicacion id="ae-ubicacion" valor={ubic} onElegir={setUbic}
       idCategoria={cat} nombreCategoria="Libros" idProducto={id} />
   `valor` y lo que recibe `onElegir` son la ubicación en la forma del
   selector (`id`, `codigo`, `nombre`, `ruta_texto`…), o null.
   ===================================================================== */
export default function SelectorUbicacion({
  id = 'cv-ubicacion', etiqueta = 'Ubicación', valor, onElegir, idCategoria, nombreCategoria,
  idProducto,
}) {
  const [abierta, setAbierta] = useState(false);
  const [sugerida, setSugerida] = useState(null);
  const cerrar = useCallback(() => setAbierta(false), []);
  useCapa(abierta, cerrar);

  // La sugerida se pide sin abrir la hoja: es lo que hace que «ubicar» cueste
  // un toque. Solo si no hay ya una elegida.
  useEffect(() => {
    if (valor || idCategoria == null) return undefined;
    let vivo = true;
    api.get(urlDelSelector({ idCategoria, idProducto }))
      .then((r) => { if (vivo) setSugerida(r?.sugerida || null); })
      .catch(() => { if (vivo) setSugerida(null); });
    return () => { vivo = false; };
  }, [valor, idCategoria, idProducto]);

  const elegir = (u) => { onElegir?.(u); setAbierta(false); };

  return (
    <div data-catalogo-vivo="selector-ubicacion">
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700">{etiqueta}</label>
      <button id={id} type="button" onClick={() => setAbierta(true)}
        className="w-full min-h-12 mt-1 inline-flex items-center gap-2 bg-white border border-gray-300 text-left text-gray-800 rounded-xl px-3">
        <MapPin className="h-5 w-5 shrink-0 text-gray-500" aria-hidden="true" />
        <span className="flex-1 break-words">{valor ? rutaLegible(valor) : 'Elige dónde va'}</span>
        <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true" />
      </button>
      {!valor && sugerida && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-catalogo-vivo="sugerida">
          <Sparkles className="h-4 w-4 text-gray-500" aria-hidden="true" />
          <span className="text-gray-700">
            Sugerida: <strong>{rutaLegible(sugerida)}</strong>
            <span className="block text-xs text-gray-500">{textoSugerida(sugerida, nombreCategoria)}</span>
          </span>
          <button type="button" onClick={() => elegir(sugerida)}
            className="min-h-11 inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
            <Check className="h-4 w-4" aria-hidden="true" /> Usar la sugerida
          </button>
        </div>
      )}
      <Ayuda id="catalogo_vivo.selector.campo" />
      {abierta && (
        <Hoja onCerrar={cerrar} onElegir={elegir} idCategoria={idCategoria} idProducto={idProducto}
          inicio={valor?.id_padre ?? null} />
      )}
    </div>
  );
}

function Hoja({ onCerrar, onElegir, idCategoria, idProducto, inicio }) {
  const [padre, setPadre] = useState(inicio);
  const [q, setQ] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [nueva, setNueva] = useState(false);
  const [intento, setIntento] = useState(0);
  const buscador = useRef(null);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      setCargando(true);
      api.get(urlDelSelector({ q, padre, idCategoria, idProducto }))
        .then((r) => { if (vivo) { setDatos(r); setError(''); } })
        .catch((e) => {
          if (!vivo) return;
          // Si el nivel ya no existe (se dio de baja o se fusionó), se vuelve
          // al inicio en vez de dejar a la persona ante un error sin salida.
          if (e?.status === 404 && padre != null) { setPadre(null); return; }
          setError(e?.message || 'No se pudieron leer las ubicaciones.');
        })
        .finally(() => { if (vivo) setCargando(false); });
    }, q ? ESPERA_BUSQUEDA_MS : 0);
    return () => { vivo = false; clearTimeout(t); };
  }, [q, padre, idCategoria, idProducto, intento]);

  const entrar = (u) => { setQ(''); setNueva(false); setPadre(u.id); };
  const lista = q.trim() ? (datos?.resultados || []) : (datos?.hijos || []);
  const actual = datos?.actual || null;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="cv-selector-titulo"
      className="fixed inset-0 z-50 bg-black/40 flex sm:items-center sm:justify-center"
      data-catalogo-vivo="hoja-ubicacion">
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[85vh] sm:max-w-lg sm:rounded-2xl flex flex-col">
        <div className="flex items-center gap-2 p-4 border-b border-gray-200">
          <h2 id="cv-selector-titulo" className="flex-1 text-lg font-semibold text-gray-900">Elige la ubicación</h2>
          <button type="button" onClick={onCerrar} aria-label="Cerrar sin elegir"
            className="min-h-11 min-w-11 inline-flex items-center justify-center gap-1 rounded-xl text-gray-700">
            <X className="h-5 w-5" aria-hidden="true" /> <span className="text-sm">Cerrar</span>
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <Ayuda id="catalogo_vivo.selector.pantalla" />
          <label htmlFor="cv-buscar" className="block text-sm font-semibold text-gray-700">Buscar en cualquier nivel</label>
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
            <input id="cv-buscar" ref={buscador} type="search" className="entrada pl-10" value={q}
              placeholder="Por ejemplo: caja res" autoComplete="off" enterKeyHint="search"
              onChange={(e) => setQ(e.target.value)} />
          </div>
          <Ayuda id="catalogo_vivo.selector.buscar" />

          {!q.trim() && (
            <nav aria-label="Dónde estás" className="flex flex-wrap items-center gap-1 text-sm" data-catalogo-vivo="migas">
              {migas(actual).map((m, i, todas) => (
                <span key={`${m.padre}`} className="inline-flex items-center gap-1">
                  {i < todas.length - 1 ? (
                    <button type="button" onClick={() => { setNueva(false); setPadre(m.padre); }}
                      className="min-h-11 px-1 underline text-[var(--color-marca-oscura)]">{m.etiqueta}</button>
                  ) : <span className="min-h-11 inline-flex items-center px-1 font-semibold text-gray-900">{m.etiqueta}</span>}
                  {i < todas.length - 1 && <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />}
                </span>
              ))}
            </nav>
          )}

          {!q.trim() && actual && (
            <div className="space-y-1">
              <button type="button" disabled={!actual.elegible} onClick={() => onElegir(actual)}
                className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4">
                <Check className="h-5 w-5" aria-hidden="true" /> Elegir aquí: {actual.nombre}
              </button>
              {!actual.elegible && <p className="text-xs text-gray-600">{actual.motivo}</p>}
            </div>
          )}

          {!q.trim() && padre == null && (datos?.recientes || []).length > 0 && (
            <section aria-labelledby="cv-recientes" className="space-y-1" data-catalogo-vivo="recientes">
              <h3 id="cv-recientes" className="text-sm font-semibold text-gray-700 inline-flex items-center gap-1">
                <Clock className="h-4 w-4" aria-hidden="true" /> Las que usaste hace poco
              </h3>
              <Filas lista={datos.recientes} onEntrar={entrar} onElegir={onElegir} conRuta />
            </section>
          )}

          {error && (
            <div className="text-sm text-red-700 space-y-2" role="alert">
              <p>{error}</p>
              <button type="button" onClick={() => setIntento((n) => n + 1)}
                className="min-h-11 inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
                Volver a intentarlo
              </button>
            </div>
          )}

          <section aria-label={q.trim() ? 'Resultados' : 'Lugares en este nivel'} className="space-y-1">
            {cargando && !datos ? (
              <div className="space-y-2" aria-live="polite" aria-busy="true">
                <span className="sr-only">Cargando ubicaciones…</span>
                <div className="h-12 rounded bg-gray-100 animate-pulse" />
                <div className="h-12 rounded bg-gray-100 animate-pulse" />
              </div>
            ) : lista.length === 0 && !error ? (
              <p className="text-sm text-gray-600" data-catalogo-vivo="vacio">
                {q.trim()
                  ? 'Ningún lugar se llama así. Prueba con otra palabra o crea uno nuevo aquí abajo.'
                  : actual
                    ? 'No hay lugares dentro de éste todavía. Puedes elegirlo o crear uno dentro.'
                    : 'Tu empresa todavía no tiene ubicaciones. Créalas en Ubicaciones.'}
              </p>
            ) : (
              <Filas lista={lista} onEntrar={entrar} onElegir={onElegir} conRuta={!!q.trim()} />
            )}
            {cargando && datos && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Cargando" />}
          </section>
        </div>

        <div className="p-4 border-t border-gray-200 space-y-2">
          {nueva && actual ? (
            <NuevaAqui padre={actual} tipoPropuesto={datos?.tipo_propuesto} nombreInicial={q.trim()}
              onCancelar={() => setNueva(false)} onCreada={onElegir} />
          ) : (
            <>
              <button type="button" disabled={!datos?.puede_crear || !!q.trim()}
                onClick={() => setNueva(true)} data-catalogo-vivo="nueva-aqui"
                className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-white border border-gray-300 disabled:opacity-60 text-gray-800 font-semibold rounded-xl px-4">
                <Plus className="h-5 w-5" aria-hidden="true" />
                {actual ? `Nueva ubicación dentro de ${actual.nombre}` : 'Nueva ubicación aquí'}
              </button>
              {datos && (!datos.puede_crear || q.trim()) && (
                <p className="text-xs text-gray-600" data-catalogo-vivo="motivo-nueva">
                  {q.trim() ? 'Para crear un lugar, borra la búsqueda y entra al lugar donde va.' : datos.motivo_no_crear}
                </p>
              )}
              <Ayuda id="catalogo_vivo.selector.nueva" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Filas({ lista, onEntrar, onElegir, conRuta = false }) {
  return (
    <ul className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
      {lista.map((u) => {
        const accion = accionDeFila(u);
        return (
          <li key={u.id} className="flex items-center gap-2 p-2" data-ubicacion={u.codigo}>
            <button type="button" disabled={accion === 'nada'}
              onClick={() => (accion === 'entrar' ? onEntrar(u) : onElegir(u))}
              className="flex-1 min-h-11 text-left disabled:opacity-60">
              <span className="block font-semibold text-gray-900 break-words">{u.nombre}</span>
              <span className="block text-xs text-gray-600 break-words">
                {conRuta ? rutaLegible(u) : (u.tipo_nombre || u.tipo)}
                {u.hijos > 0 ? ` · ${u.hijos} dentro` : ''}
              </span>
              {!u.elegible && <span className="block text-xs text-gray-500">{u.motivo}</span>}
            </button>
            {accion === 'entrar' && u.elegible && (
              <button type="button" onClick={() => onElegir(u)}
                className="min-h-11 inline-flex items-center gap-1 bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
                Elegir
              </button>
            )}
            {accion === 'entrar' && (
              <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ul>
  );
}

function NuevaAqui({ padre, tipoPropuesto, nombreInicial, onCancelar, onCreada }) {
  const [nombre, setNombre] = useState(nombreInicial || '');
  const [tipo, setTipo] = useState(tipoPropuesto || 'ESTANTE');
  const [parecidas, setParecidas] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const corto = nombre.trim().length < 2;

  async function crear(aunqueSeParezca = false) {
    setGuardando(true);
    setError('');
    try {
      const r = await api.post(RUTA, {
        nombre: nombre.trim(), id_padre: padre.id, tipo, crear_aunque_se_parezca: aunqueSeParezca,
      });
      if (r?.creada) onCreada(r.ubicacion);
      else setParecidas(r?.parecidas || []);
    } catch (e) {
      setError(e?.message || 'No se pudo crear la ubicación. Revisa el nombre y vuelve a intentarlo.');
    } finally {
      setGuardando(false);
    }
  }

  if (parecidas) {
    return (
      <div className="space-y-2" data-catalogo-vivo="es-esta">
        <p className="text-sm font-semibold text-gray-900">¿Es alguna de éstas? Ya hay lugares parecidos en {padre.nombre}.</p>
        <ul className="space-y-1">
          {parecidas.map((p) => (
            <li key={p.id}>
              <button type="button" disabled={!p.activo}
                onClick={() => onCreada({ ...p, id_padre: padre.id,
                  ruta_texto: `${rutaLegible(padre)} > ${p.nombre}` })}
                className="w-full min-h-11 inline-flex items-center gap-2 bg-white border border-gray-300 disabled:opacity-60 text-gray-800 rounded-xl px-3 text-left">
                <Check className="h-4 w-4" aria-hidden="true" /> Sí, es {p.nombre}
                {!p.activo && <span className="text-xs text-gray-500"> (dada de baja: reactívala en Ubicaciones)</span>}
              </button>
            </li>
          ))}
        </ul>
        <button type="button" disabled={guardando} onClick={() => crear(true)}
          className="w-full min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-4">
          {guardando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
          No, crear «{nombre.trim()}»
        </button>
        {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <form className="space-y-2" data-catalogo-vivo="form-nueva"
      onSubmit={(e) => { e.preventDefault(); if (!corto) crear(false); }}>
      <p className="text-sm font-semibold text-gray-900">Nueva ubicación dentro de {padre.nombre}</p>
      <label htmlFor="cv-nueva-nombre" className="block text-sm font-semibold text-gray-700">Nombre</label>
      <input id="cv-nueva-nombre" className="entrada" value={nombre} autoComplete="off"
        placeholder="Por ejemplo: Caja de tornillos" onChange={(e) => setNombre(e.target.value)} />
      <label htmlFor="cv-nueva-tipo" className="block text-sm font-semibold text-gray-700">Qué es</label>
      <select id="cv-nueva-tipo" className="entrada" value={tipo} onChange={(e) => setTipo(e.target.value)}>
        {TIPOS_AL_VUELO.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
      </select>
      <Ayuda id="catalogo_vivo.selector.tipo" />
      <div className="flex gap-2">
        <button type="button" onClick={onCancelar}
          className="flex-1 min-h-12 inline-flex items-center justify-center bg-white border border-gray-300 text-gray-800 font-semibold rounded-xl px-3">
          Cancelar
        </button>
        <button type="submit" disabled={corto || guardando}
          className="flex-1 min-h-12 inline-flex items-center justify-center gap-2 bg-[var(--color-marca)] disabled:opacity-60 text-white font-semibold rounded-xl px-3">
          {guardando ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Plus className="h-5 w-5" aria-hidden="true" />}
          Crear y elegir
        </button>
      </div>
      {corto && <p className="text-xs text-gray-600">Escribe el nombre del lugar (al menos 2 letras) para crearlo.</p>}
      {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
    </form>
  );
}
